/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MessagesDataAssetsDataAsset } from '../models/MessagesDataAssetsDataAsset';
import type { MessagesDataAssetsNewDataAsset } from '../models/MessagesDataAssetsNewDataAsset';
import type { MessagesDataAssetsTDataAssetActivity } from '../models/MessagesDataAssetsTDataAssetActivity';
import type { SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues } from '../models/SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DataAssetApiService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Adds a new data asset. (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param requestBody The details of the new data asset to add.
     * @returns MessagesDataAssetsDataAsset OK
     * @throws ApiError
     */
    public putDataAssetApiAddDataAsset(
        requestBody?: MessagesDataAssetsNewDataAsset,
    ): CancelablePromise<MessagesDataAssetsDataAsset> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/data_asset',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Updates a data asset. (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param requestBody The details of the updated data asset to add.
     * @returns MessagesDataAssetsDataAsset OK
     * @throws ApiError
     */
    public postDataAssetApiUpdateTemplate(
        requestBody?: MessagesDataAssetsDataAsset,
    ): CancelablePromise<MessagesDataAssetsDataAsset> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/data_asset',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Deletes a data asset. (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param requestBody The details of the data asset to delete, only if nothing is associated with it yet.
     * @returns MessagesDataAssetsDataAsset OK
     * @throws ApiError
     */
    public deleteDataAssetApiDeleteTemplate(
        requestBody?: MessagesDataAssetsDataAsset,
    ): CancelablePromise<MessagesDataAssetsDataAsset> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/data_asset',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * List data assets for an application / entity type (Authorised Roles: SuperUser, ApplicationAdministrator)
     * @param applicationId App id, always required
     * @param entityTypeId Optional entity type id, otherwise all data assets for app
     * @returns MessagesDataAssetsDataAsset OK
     * @throws ApiError
     */
    public getDataAssetApiListDataAssets(
        applicationId: number,
        entityTypeId?: number,
    ): CancelablePromise<Array<MessagesDataAssetsDataAsset>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/data_asset',
            query: {
                'applicationId': applicationId,
                'entityTypeId': entityTypeId,
            },
        });
    }

    /**
     * Gets the summary of data assets for a specific application and project.
     * @param applicationId The application ID.
     * @param projectId The project ID.
     * @returns MessagesDataAssetsTDataAssetActivity OK
     * @throws ApiError
     */
    public getDataAssetApiGetTemplateSummary(
        applicationId: number,
        projectId: number,
    ): CancelablePromise<Array<MessagesDataAssetsTDataAssetActivity>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/data_asset/summary/{applicationId},{projectId}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
            },
        });
    }

    /**
     * Gets the activity log of a specific data asset.
     * @param applicationId The application ID.
     * @param projectId The project ID.
     * @param entityId The entity ID.
     * @param dataAssetId The data asset ID.
     * @returns MessagesDataAssetsTDataAssetActivity OK
     * @throws ApiError
     */
    public getDataAssetApiGetSpecificDataAssetSummary(
        applicationId: number,
        projectId: number,
        entityId: number,
        dataAssetId: number,
    ): CancelablePromise<Array<MessagesDataAssetsTDataAssetActivity>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/data_asset/activity_log/{applicationId},{projectId},{entityId},{dataAssetId}',
            path: {
                'applicationId': applicationId,
                'projectId': projectId,
                'entityId': entityId,
                'dataAssetId': dataAssetId,
            },
        });
    }

    /**
     * Updates the state of a data asset.
     * @param formData The form data.
     * @returns MessagesDataAssetsTDataAssetActivity OK
     * @throws ApiError
     */
    public postDataAssetApiPostTransitionUpdate(
        formData?: {
            formData?: Array<SystemCollectionsGenericKeyValuePairOfSystemStringMicrosoftExtensionsPrimitivesStringValues>;
        },
    ): CancelablePromise<Array<MessagesDataAssetsTDataAssetActivity>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/data_asset/transition',
            formData: formData,
            mediaType: 'multipart/form-data',
        });
    }

}
